/**
 * AgentListView -- Agent interaction hub with n8n integration.
 * Chat-first layout: prominent chat area with smart response rendering.
 * Actions: Pensadero, Analizar (Router), Crear mision (Chat/Patronus).
 * Logging: feedback thumbs + effectiveness stats.
 *
 * @module presentation/views/AgentListView
 */

import { AgentApiClient } from '../../infrastructure/api/AgentApiClient.js';
import { InstructionApiClient } from '../../infrastructure/api/InstructionApiClient.js';
import { ProjectApiClient } from '../../infrastructure/api/ProjectApiClient.js';
import { pedirPuertaEnDialogo, claveSugerida } from '../shared/camposPuerta.js';
import { motivoPuerta } from '../../application/tareas/puertaTarea.js';
import { MissionApiClient } from '../../infrastructure/api/MissionApiClient.js';
import { TaskApiClient } from '../../infrastructure/api/TaskApiClient.js';
import { HandoffApiClient } from '../../infrastructure/api/HandoffApiClient.js';
import { BitacoraApiClient } from '../../infrastructure/api/BitacoraApiClient.js';
import { RagApiClient } from '../../infrastructure/api/RagApiClient.js';
import { AgentBadgeApiClient } from '../../infrastructure/api/AgentBadgeApiClient.js';
import { AgentInteractionLog } from '../../application/AgentInteractionLog.js';
import { renderMarkdown } from '../shared/DocumentViewer.js';
import { renderResumenPorPersona } from '../shared/NxResumenPorPersona.js';
import { bindDiaAcciones } from '../shared/NxCorreccionMarcaje.js';
import { renderInformeVertical } from '../shared/NxInformeVertical.js';
import { getSelectedBusiness, getSelectedBusinessName } from '../components/BusinessSelector.js';
import { contextoDeConsulta, FRASE_SIN_DATOS, negocioCambio, fraseNegocioCambio } from '../../application/asistente/contextoDeRespuesta.js';
import { contextoRespuestaHTML } from '../shared/NxContextoRespuesta.js';
import { createActionArea } from '../components/ActionArea.js';
import { parseSlashCommand, isWatcherActive, setWatcherActive, getSlashCommandRegistry } from '../components/SlashCommands.js';

const STATUS_COLORS = { active: '#238636', inactive: '#6e7681', archived: '#6e7681' };

/**
 * Construye una tabla HTML reutilizable para rows del response de Consulta.
 * Se usa tanto en el render directo como en el <details> "Ver tabla tecnica".
 */
function buildConsultaTable(rows) {
  if (!rows || rows.length === 0) {
    return '<div style="color:#8b949e;font-size:0.875rem">Sin filas.</div>';
  }
  const cols = Object.keys(rows[0]);
  const thCells = cols.map(c => `<th style="padding:4px 8px;text-align:left;border-bottom:1px solid #30363d;font-size:0.875rem;color:#8b949e;white-space:nowrap">${c}</th>`).join('');
  const bodyRows = rows.map(r => {
    const tds = cols.map(c => {
      let v = r[c] ?? '-';
      if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) v = v.substring(0, 10);
      const style = c === 'status'
        ? `color:${v === 'in_progress' ? '#f0883e' : v === 'active' ? '#58a6ff' : v === 'completed' ? '#3fb950' : v === 'pending' ? '#d29922' : '#8b949e'}`
        : c === 'reward_nexos' || c === 'nxs_total' ? 'color:#d2a8ff;font-weight:600' : '';
      return `<td style="padding:4px 8px;border-bottom:1px solid #21262d;font-size:0.875rem;${style}">${String(v).replace(/</g,'&lt;')}</td>`;
    }).join('');
    return `<tr>${tds}</tr>`;
  }).join('');
  return `<div style="overflow-x:auto"><table style="border-collapse:collapse;width:100%"><thead><tr>${thCells}</tr></thead><tbody>${bodyRows}</tbody></table></div>`;
}

let sessionId = `panel-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
let n8nConnected = null;
let cachedProjects = [];
let selectedAgentIdx = 0;
let allAgents = [];

// ── UIA-T1 (bit #3729): modo dueno vs modo desarrollador ──────────────────────
// Diagnostico de Ares: la pantalla mezcla tres publicos en un mismo plano. Del
// dueno solo importan la caja de preguntar y la respuesta; las 25 instrucciones,
// la efectividad, el pensadero, los iconos sin etiqueta y las tarjetas de agentes
// son NUESTRAS, no suyas.
//
// Regla del ASSIGN: NADA se borra, todo se MUEVE. El modo dev conserva la pantalla
// completa de hoy porque nos sigue sirviendo. El default es dueno.
// Deuda 4: era una copia del escape; ahora es el compartido.
import { esc as escapeHtmlAgente } from '../shared/texto.js';
import { regresoHtml } from '../shared/regreso.js';
import { formatearFecha } from '../shared/fechaColombiana.js';

/**
 * UIA-T2 -- decide si lo que llego es de verdad una FRASE que el dueno pueda leer.
 *
 * El panel no arma la narrativa: n8n resuelve narrative_template contra el resultado
 * del SELECT y manda el texto ya hecho. El problema de la captura de Josse es que
 * cuando eso sale mal igual llega un string, y el panel lo pintaba como si fuera la
 * respuesta. Hay dos formas de que salga mal y ninguna se veia:
 *
 *  1. La plantilla queda a medio resolver. Caso real: ingresos.total_periodo pide
 *     {negocio_nombre} y su propio SELECT no devuelve esa columna, asi que el
 *     placeholder sobrevive literal en la frase.
 *  2. n8n manda su texto generico de respaldo, que nombra el codigo interno de la
 *     capacidad ("Encontre 1 resultado ... para ingresos.total_periodo"). Al dueno
 *     ese codigo no le dice nada: es nomenclatura nuestra, no una respuesta.
 *
 * Devuelve el motivo concreto para poder decir CUAL fallo, no solo que fallo.
 */
function diagnosticarNarrativa(narrative, capabilityUsed) {
  const txt = String(narrative ?? '').trim();
  if (!txt) return { esFrase: false, motivo: 'la respuesta no trajo narrativa' };

  // Placeholders sin resolver: {algo_asi}. Se exige guion bajo o palabra suelta para
  // no confundirse con un texto que legitimamente use llaves.
  const pendientes = [...new Set((txt.match(/\{[a-z0-9_]+\}/gi) || []))];
  if (pendientes.length > 0) {
    return {
      esFrase: false,
      motivo: `la plantilla dejó sin resolver ${pendientes.join(', ')} — el SELECT de la capacidad no devuelve esa columna`
    };
  }

  // Texto generico de n8n: menciona el codigo dominio.intento de la capacidad.
  const codigo = String(capabilityUsed ?? '').trim();
  if (codigo && txt.includes(codigo)) {
    return { esFrase: false, motivo: `n8n devolvió su texto de respaldo, que nombra el código interno "${codigo}" en vez de contar el dato` };
  }
  if (/\bpara\s+[a-z_]+\.[a-z_]+\b/i.test(txt)) {
    return { esFrase: false, motivo: 'la narrativa nombra un código de capacidad en vez de contar el dato' };
  }

  return { esFrase: true, motivo: '' };
}

const UI_MODE_KEY = 'nx_agent_ui_mode';
function getUiMode() {
  try {
    return localStorage.getItem(UI_MODE_KEY) === 'dev' ? 'dev' : 'duenio';
  } catch { return 'duenio'; }
}
function setUiMode(mode) {
  try { localStorage.setItem(UI_MODE_KEY, mode === 'dev' ? 'dev' : 'duenio'); } catch { /* no-op */ }
}
const isDevMode = () => getUiMode() === 'dev';

// UIA-T3 (bit #3729): preguntas de arranque. El dueno no sabe que preguntar y una
// caja en blanco no ayuda. Escritas como las diria el, no con el nombre tecnico.
//
// FUENTE: la prueba de humo AV-T2 (bitacora #3732, PR nexo-api #130) barrio las 30
// capacidades activas contra la BD real: 21 OK / 9 delatadas. Las 4 de abajo mapean
// SOLO a capacidades que devolvieron DATOS REALES en ese barrido:
//   asistencia.tarde_rango        (54 filas)  -> "¿Quién llegó tarde hoy?"
//   asistencia.incompletos_rango  (21 filas)  -> "¿A quién le faltó marcar?"
//   asistencia.horas_trabajadas   (6 filas)   -> "¿Cuántas horas trabajó el equipo?"
//   asistencia.auditoria_rango    (132 filas) -> "¿Cómo estuvo la asistencia esta semana?"
//
// NO se ofrece "¿Cómo vamos hoy?" aunque exista reporte.como_nos_fue_hoy: esa
// capacidad es una de las 9 DELATADAS (nunca recibe el negocio), asi que le
// devolveria un vacio al dueno -- justo lo que esta mision quiere evitar. Se agrega
// cuando AV-T1 arregle el parametro. Tampoco se ofrecen misiones/tareas/bitacora:
// funcionan, pero son operativas NUESTRAS, no del dueno de un negocio.
// ACTUALIZACION (bit #3759): Ares aplico el UPDATE del parametro en produccion y las
// 9 capacidades delatadas quedaron sanas (verificado en BD: 0 rotas de 30 activas).
// Se suman las 2 financieras que estaban esperando, y se reordena para que lo primero
// que vea el dueno sea SU negocio -- no la asistencia del equipo.
// Verificacion previa a tocar esta constante (no basta con que el parametro este bien
// escrito): ingresos.total_periodo corrida sobre EC devuelve 15.958.503 en 14 dias
// con data. Devuelve numeros reales, no un cero silencioso.
const PREGUNTAS_ARRANQUE = [
  '¿Cómo nos fue hoy?',              // reporte.como_nos_fue_hoy
  '¿Cuánto vendimos este mes?',      // ingresos.total_periodo
  '¿Quién llegó tarde hoy?',         // asistencia.tarde_rango
  '¿Cómo estuvo la asistencia esta semana?'  // asistencia.auditoria_rango
];

function showHandoffModal(prompt, agentName) {
  let modal = document.getElementById('nx-handoff-modal');
  if (modal) modal.remove();
  modal = document.createElement('div');
  modal.id = 'nx-handoff-modal';
  modal.style.cssText = 'position:fixed;inset:0;background:rgba(0,0,0,0.7);z-index:1000;display:flex;align-items:center;justify-content:center;padding:20px';
  modal.innerHTML = `
    <div style="background:#161b22;border:1px solid #30363d;border-radius:10px;padding:20px;max-width:700px;width:100%;max-height:90vh;overflow-y:auto">
      <div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px">
        <h3 style="margin:0;font-size:1rem">Prompt para nuevo chat ${(agentName || '').replace(/</g,'&lt;')}</h3>
        <button class="btn-ghost btn-sm" id="nx-handoff-close">&times;</button>
      </div>
      <textarea id="nx-handoff-text" readonly style="width:100%;height:280px;font-family:monospace;font-size:0.875rem;background:#0d1117;border:1px solid #30363d;color:#c9d1d9;padding:10px;border-radius:6px;resize:vertical">${(prompt || '').replace(/</g,'&lt;')}</textarea>
      <div style="display:flex;gap:8px;margin-top:10px;justify-content:flex-end">
        <button class="btn-primary btn-sm" id="nx-handoff-copy">\uD83D\uDCCB Copiar al portapapeles</button>
      </div>
      <p id="nx-handoff-msg" style="font-size:0.875rem;color:#238636;margin-top:6px;text-align:right;height:16px"></p>
    </div>`;
  document.body.appendChild(modal);
  document.getElementById('nx-handoff-close').onclick = () => modal.remove();
  modal.addEventListener('click', e => { if (e.target === modal) modal.remove(); });
  document.getElementById('nx-handoff-copy').onclick = async () => {
    const text = document.getElementById('nx-handoff-text').value;
    try {
      await navigator.clipboard.writeText(text);
      document.getElementById('nx-handoff-msg').textContent = '\u2713 Copiado';
    } catch {
      document.getElementById('nx-handoff-text').select();
      document.execCommand('copy');
      document.getElementById('nx-handoff-msg').textContent = '\u2713 Copiado';
    }
  };
}

function badge(status) {
  const c = STATUS_COLORS[status] || '#6e7681';
  return `<span class="status-badge" style="background:${c}22;color:${c}">${status}</span>`;
}

function connDot() {
  if (n8nConnected === null) return '<span class="conn-dot conn-unknown" title="Verificando..."></span>';
  return n8nConnected
    ? '<span class="conn-dot conn-ok" title="n8n conectado"></span>'
    : '<span class="conn-dot conn-off" title="Modo local"></span>';
}

export async function renderAgentList(container) {
  container.innerHTML = '<p class="empty-state">Cargando agentes...</p>';

  const [agentsRes, instructionsRes, projectsRes, missionsRes] = await Promise.all([
    AgentApiClient.list(1, 20),
    InstructionApiClient.list(1, 100),
    ProjectApiClient.list(1, 50),
    MissionApiClient.list(1, 100)
  ]);

  allAgents = agentsRes.data?.data?.items || agentsRes.data?.items || [];
  // UIA-T1 (bit #3729): en modo dueno el selector de agentes esta oculto, asi que la
  // vista SIEMPRE debe abrir en Argemiro -- es con quien el dueno habla. Sin esto, si
  // el primer agente de la lista fuera ARES la pantalla quedaria en una bitacora sin
  // caja de preguntar y sin forma visible de cambiar de agente.
  if (!isDevMode()) {
    const idxArgemiro = allAgents.findIndex(a => a.code === 'ARGEMIRO-001');
    if (idxArgemiro >= 0) selectedAgentIdx = idxArgemiro;
  }
  const instructions = instructionsRes.data?.data?.items || instructionsRes.data?.items || [];
  cachedProjects = projectsRes.data?.data?.items || projectsRes.data?.items || [];
  const allMissions = missionsRes.data?.data?.items || missionsRes.data?.items || [];

  // Contar misiones activas por agente
  const missionCountByAgent = {};
  for (const mi of allMissions) {
    if (mi.status === 'completed' || mi.status === 'archived') continue;
    const match = mi.description?.match(/\[AGENTE:\s*([^\]]+)\]/i);
    if (match) {
      match[1].split(',').map(a => a.trim()).filter(Boolean).forEach(code => {
        missionCountByAgent[code] = (missionCountByAgent[code] || 0) + 1;
      });
    }
  }

  if (!allAgents.length) {
    container.innerHTML = `
      <section class="artifact-view">
        ${regresoHtml()}
        <h1>Agentes</h1>
        <p class="empty-state">No hay agentes registrados</p>
      </section>`;
    return;
  }

  // Asegurar Argemiro como primer agente (tiene chat n8n)
  const argemiroIdx = allAgents.findIndex(a => a.code === 'ARGEMIRO-001');
  if (argemiroIdx > 0) {
    const [a] = allAgents.splice(argemiroIdx, 1);
    allAgents.unshift(a);
  }

  selectedAgentIdx = 0;
  const stats = AgentInteractionLog.getStats();

  container.innerHTML = `
    <section class="artifact-view">
      <div style="display:flex;justify-content:space-between;align-items:center;gap:10px;flex-wrap:wrap">
        ${regresoHtml()}
        <div style="display:flex;gap:8px;align-items:center">
          ${isDevMode() ? `<button id="nx-handoff-btn" class="btn-ghost btn-sm" style="font-size:0.875rem">&#128203; Copiar prompt handoff</button>` : ''}
          <!-- UIA-T1: el interruptor vive arriba y dice en que modo estas. En modo
               dueno se lee como una opcion tecnica opcional, no como algo que deba
               tocar para usar el chat. -->
          <button id="nx-ui-mode-toggle" class="btn-ghost btn-sm" style="font-size:0.875rem;color:#8b949e"
                  title="${isDevMode() ? 'Volver a la vista simple' : 'Ver herramientas de desarrollo (instrucciones, efectividad, pensadero)'}">
            ${isDevMode() ? '&#128065; Modo desarrollador' : '&#9881; Modo simple'}
          </button>
        </div>
      </div>

      <!-- UIA-T1: el selector de agentes es herramienta nuestra. El dueno habla con
           Argemiro y punto; ver tarjetas de ARES/ARGYS solo agrega ruido. -->
      <div class="agent-selector" id="nx-agent-selector" ${isDevMode() ? '' : 'style="display:none"'}>
        ${allAgents.map((a, idx) => {
          const mc = missionCountByAgent[a.code] || 0;
          return `
          <button class="agent-tab ${idx === 0 ? 'agent-tab-active' : ''}" data-agent-idx="${idx}">
            <span class="agent-tab-avatar" style="background:${idx === 0 ? 'linear-gradient(135deg,#238636,#58a6ff)' : 'linear-gradient(135deg,#6e7681,#30363d)'}">${(a.name || '?')[0]}</span>
            <span class="agent-tab-name">${a.name}</span>
            ${badge(a.status)}
            ${mc > 0 ? `<span class="agent-mission-count" title="${mc} mision${mc !== 1 ? 'es' : ''} activa${mc !== 1 ? 's' : ''}">&#9733; ${mc}</span>` : ''}
          </button>`;
        }).join('')}
      </div>

      <div id="nx-agent-content"></div>
      <!-- PANEL-AUDIOS-T1 (bit #8233): historial de audios de Argemiro.
           AUDIENCIA: panel completo (dueno/Nexo) — NUNCA la tablet (regla #5638). -->
      <div id="nx-audios-argemiro"></div>
    </section>`;

  // Boton Copiar prompt handoff — prompts locales por agente
  document.getElementById('nx-handoff-btn')?.addEventListener('click', async () => {
    const currentAgent = allAgents[selectedAgentIdx];
    const code = currentAgent?.code || 'ARES-001';
    const P = 'C:\\Proyectos\\Nexo Universe';
    const INS = P + '\\Nexo Center\\Docs\\7_Instrucciones';
    const prompts = {
      'ARES-001': 'Eres Ares (ARES-001), (El mejor agente de toda la historia), orquestador del ecosistema Nexo Universe para Nexo Digital.\nResponde siempre en español.\n\nLee e inicializa segun:\n' + INS + '\\shared\\INIT.md\n\nINIT.md te dira en que orden leer las demas instrucciones, como establecer el tunel SSH,\nleer la bitacora, y comenzar a operar como Ares.\n\nAl terminar la inicializacion, reporta estado de misiones in_progress y sugiere siguiente accion.\n\nRespondes ante Josse, (Conocido Narrativamente como el Fenix)',
      'ARGEMIRO-001': 'Eres Argemiro (ARGEMIRO-001), agente director del ecosistema Nexo Universe.\nResponde siempre en español. Tienes memoria persistente y capacidades operativas via modo Consulta.\n\nCapacidades disponibles (modo Consulta del panel):\n- Misiones: contar, listar activas, estancadas, por agente, prioridad\n- Tareas: contar, pendientes, por mision, huerfanas, completadas\n- Bitacora: recientes, por agente, por marcador\n- Sistema: que puedo hacer',
      'ARGYS-001': 'Eres Argys (ARGYS-001), agente desktop del ecosistema Nexo Universe.\nResponde siempre en español.\nLee: ' + INS + '\\shared\\CONTEXT.md\nLee: ' + INS + '\\desktop\\DESIGN.md\nGit pull en nexo_desktop antes de empezar.'
    };
    const prompt = prompts[code] || prompts['ARES-001'];
    const modalTitle = code === 'ARGEMIRO-001' ? 'Argemiro' : code === 'ARGYS-001' ? 'Argys' : 'Ares';
    showHandoffModal(prompt, modalTitle);
  });

  // UIA-T1 (bit #3729): interruptor de modo. Re-renderiza la vista entera para que
  // el cambio aplique a todo (encabezado, selector, sidebar y respuestas).
  document.getElementById('nx-ui-mode-toggle')?.addEventListener('click', () => {
    setUiMode(isDevMode() ? 'duenio' : 'dev');
    renderAgentList(container);
  });

  // Delegar clicks en selector de agentes
  document.getElementById('nx-agent-selector')?.addEventListener('click', (e) => {
    const tab = e.target.closest('[data-agent-idx]');
    if (!tab) return;
    const idx = parseInt(tab.dataset.agentIdx, 10);
    if (idx === selectedAgentIdx) return;
    selectedAgentIdx = idx;
    document.querySelectorAll('.agent-tab').forEach((t, i) =>
      t.classList.toggle('agent-tab-active', i === idx));
    renderAgentContent(instructions, stats);
  });

  renderAgentContent(instructions, stats);

  // PANEL-AUDIOS-T1 fase A: seccion ADITIVA y fail-open. Import dinamico para no
  // cargarla en quien no abre esta vista; si algo falla, la vista de agentes sigue igual.
  import('../shared/NxAudiosArgemiro.js')
    .then(m => m.renderAudiosArgemiro('nx-audios-argemiro'))
    .catch(() => { /* seccion opcional: nunca rompe la vista */ });
}

function renderAgentContent(instructions, stats) {
  const contentEl = document.getElementById('nx-agent-content');
  if (!contentEl) return;
  const agent = allAgents[selectedAgentIdx];
  const isChat = agent.code === 'ARGEMIRO-001';

  // UIA-T1 (bit #3729): en modo dueno el hero se reduce a nombre + una linea de que
  // hace. Fuera el codigo tecnico del agente, el badge de status y la fila de badges
  // de capacidades (los 8 iconos sin etiqueta que Ares marco como ruido).
  const heroHtml = isDevMode()
    ? `
    <div class="agent-hero">
      <div class="agent-avatar-lg" style="background:${isChat ? 'linear-gradient(135deg,#238636,#58a6ff)' : 'linear-gradient(135deg,#6e7681,#30363d)'}">${(agent.name || '?')[0]}</div>
      <div class="agent-hero-info">
        <h1>${agent.name} <code class="instruction-code">${agent.code}</code></h1>
        <p class="agent-desc">${agent.description || ''}</p>
        <a href="#/agente/${encodeURIComponent(agent.code || '')}" data-agente-ficha style="font-size:14px;color:#58a6ff">Ver su ficha: lo que sabe y lo que puede</a>
        <div class="agent-status-row">
          ${badge(agent.status)}
          ${isChat ? `<span id="nx-conn-status">${connDot()}</span>` : ''}
        </div>
        <div class="agent-badges-row" id="nx-agent-badges"></div>
      </div>
    </div>`
    : `
    <div class="agent-hero" style="align-items:center">
      <div class="agent-avatar-lg" style="background:linear-gradient(135deg,#238636,#58a6ff)">${(agent.name || '?')[0]}</div>
      <div class="agent-hero-info">
        <h1 style="margin-bottom:2px">${agent.name}</h1>
        <p class="agent-desc" style="margin:0">Pregúntale por tu negocio en palabras normales.</p>
      </div>
    </div>`;

  if (isChat) {
    // Chat agent (Argemiro) — chat con toggle RAG integrado
    contentEl.innerHTML = `
      ${heroHtml}
      <div class="agent-layout">
        <div class="agent-chat-area">
          <div id="nx-action-area"></div>
          <div class="agent-chat" id="nx-agent-chat">
            <div class="agent-messages" id="nx-agent-messages">
              <div class="agent-msg agent-msg-system">${isDevMode() ? 'Conectando a n8n...' : 'Un momento, estoy despertando...'}</div>
            </div>
            <!-- UIA-T1: la barra de acciones (pensadero, analizar, crear-mision,
                 watcher, switch de 4 modos RAG) es tablero de control nuestro. El
                 dueno solo pregunta y lee. Se OCULTA, no se borra: en modo dev sigue
                 completa y los handlers no cambian. -->
            <div class="agent-action-bar" id="nx-action-bar" ${isDevMode() ? '' : 'style="display:none"'}>
              <select id="nx-project-select" class="action-select" title="Proyecto para contexto">
                <option value="">Sin proyecto</option>
                ${cachedProjects.map(p =>
                  `<option value="${p.projectKey}">${p.projectKey} (${p.displayName || ''})</option>`
                ).join('')}
              </select>
              <button class="btn-action" data-action="pensadero" title="Pensadero: contexto del proyecto"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"/><path d="M12 16v-4M12 8h.01"/></svg></button>
              <button class="btn-action" data-action="analizar" title="Analizar proyecto"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="11" cy="11" r="8"/><path d="m21 21-4.3-4.3"/></svg></button>
              <button class="btn-action btn-action-accent" data-action="crear-mision" title="Crear mision desde notas"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 5v14M5 12h14"/></svg></button>
              <button id="nx-watcher-toggle" class="btn-action btn-watcher" type="button"
                      title="Modo Watcher: cuando esta ON, comandos slash (/factura, /diseno, /onboarding) invocan el panel de accion sin pasar por el agente">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="3"/><path d="M12 1v6M12 17v6M4.22 4.22l4.24 4.24M15.54 15.54l4.24 4.24M1 12h6M17 12h6M4.22 19.78l4.24-4.24M15.54 8.46l4.24-4.24"/></svg>
              </button>
              <div class="rag-mode-switch" id="nx-rag-mode">
                <button class="rag-mode-btn rag-mode-btn--active" data-rag-mode="chat" title="Chat normal con el agente"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/></svg></button>
                <button class="rag-mode-btn" data-rag-mode="consulta" title="Consulta operativa: misiones, tareas, bitacora"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M18 20V10M12 20V4M6 20v-6"/></svg></button>
                <button class="rag-mode-btn" data-rag-mode="rag" title="Consulta directa al vector store RAG"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M4 19.5A2.5 2.5 0 0 1 6.5 17H20"/><path d="M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"/></svg></button>
                <button class="rag-mode-btn" data-rag-mode="rag-ai" title="RAG + respuesta inteligente"><svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M12 2a4 4 0 0 0-4 4v2H6a2 2 0 0 0-2 2v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8a2 2 0 0 0-2-2h-2V6a4 4 0 0 0-4-4z"/><circle cx="9" cy="13" r="1"/><circle cx="15" cy="13" r="1"/><path d="M9 17h6"/></svg></button>
              </div>
            </div>
            <!-- UIA-T3 (bit #3729): el dueno no sabe que preguntar. En vez de una caja
                 en blanco, 4 preguntas escritas como las diria el. Al tocar una, se
                 envia -- no solo rellena el campo, que dejaria el trabajo a medias. -->
            ${isDevMode() ? '' : `
            <div id="nx-sugerencias" class="nx-consola-chips" style="padding:8px 10px 0">
              ${PREGUNTAS_ARRANQUE.map(q =>
                `<button type="button" class="nx-sug-btn nx-chip" data-q="${escapeHtmlAgente(q)}">${escapeHtmlAgente(q)}</button>`
              ).join('')}
            </div>`}
            <form class="agent-input-form" id="nx-agent-form">
              <textarea id="nx-agent-input"
                        placeholder="${isDevMode() ? 'Escribe un mensaje o notas para el agente...' : 'Escribe tu pregunta…'}"
                        class="form-input agent-textarea" rows="${isDevMode() ? 3 : 4}"
                        ${isDevMode() ? '' : 'style="font-size:0.95rem"'}></textarea>
              <button type="submit" class="btn-primary btn-sm">Enviar</button>
            </form>
          </div>
        </div>
        <!-- UIA-T1: sidebar (25 instrucciones + efectividad) = herramienta nuestra. -->
        ${isDevMode() ? `
        <div class="agent-sidebar">
          <details class="agent-sidebar-section" open>
            <summary>Instrucciones (${instructions.length})</summary>
            <div class="agent-inst-chips">
              ${instructions.length > 0
                ? instructions.slice(0, 20).map(i =>
                  `<a href="#/instructions" class="inst-chip" title="${i.title}"><code>${i.code}</code></a>`
                ).join('')
                : '<span class="empty-state" style="padding:0.5rem;font-size:0.875rem">Sin instrucciones</span>'}
              ${instructions.length > 20 ? `<a href="#/instructions" class="inst-chip inst-chip-more">+${instructions.length - 20} más</a>` : ''}
            </div>
          </details>
          ${stats.total > 0 ? renderStatsPanel(stats) : ''}
        </div>` : ''}
      </div>`;
    setupChatHandlers(agent);
    setupActionHandlers(agent);
    setupActionArea();
    setupWatcherAndSlash();
    verifyConnection(agent);
  } else {
    // Non-chat agent (Ares, Argys) — muestra bitacora
    contentEl.innerHTML = `
      ${heroHtml}
      <div id="nx-agent-bitacora" style="margin-top:16px">
        <p class="empty-state">Cargando bitacora...</p>
      </div>`;
    loadAgentBitacora(agent);
  }
  loadAgentBadges(agent.code);
}

const BADGE_DOMAIN_COLORS = {
  memoria: '#a371f7', consulta: '#58a6ff', rag: '#238636', diagnostico: '#d29922'
};

const HERO_BADGE_ICONS = {
  'Memoria Persistente': '\uD83E\uDDE0',
  'Consulta Operativa': '\u26A1',
  'Conocimiento RAG': '\uD83D\uDCDA',
  'Autodiagnostico': '\uD83D\uDD2E',
  'Gestor de Misiones': '\uD83C\uDFAF',
  'Gestor de Tareas': '\uD83D\uDCCB'
};

async function loadAgentBadges(agentCode) {
  const el = document.getElementById('nx-agent-badges');
  if (!el) return;
  const res = await AgentBadgeApiClient.list({ agentCode });
  if (!res.ok) { el.innerHTML = ''; return; }
  const badges = res.data?.data?.items || res.data?.data || [];
  if (badges.length === 0) { el.innerHTML = ''; return; }
  el.innerHTML = `<div style="display:flex;gap:6px;flex-wrap:wrap;margin-top:6px">${badges.map(b => {
    const color = BADGE_DOMAIN_COLORS[b.domain] || '#6e7681';
    const icon = HERO_BADGE_ICONS[b.name] || '\uD83C\uDFC5';
    const lvl = Math.floor(parseFloat(b.version || '1.0'));
    return `<span class="task-tag" style="background:${color}22;color:${color};font-size:0.875rem;display:inline-flex;align-items:center;gap:3px" title="${escapeHtmlAgente(b.description)}">${icon} ${escapeHtmlAgente(b.name || b.badgeCode || b.badge_code)} <span style="font-size:0.875rem;opacity:0.7">Lvl${lvl}</span></span>`;
  }).join('')}</div>`;
}

function renderStatsPanel(stats) {
  const rate = stats.helpfulRate !== null ? `${stats.helpfulRate}%` : '--';
  return `
    <details class="agent-sidebar-section" open>
      <summary>Efectividad</summary>
      <div class="stats-compact">
        <span>${stats.total} msgs</span>
        <span>${stats.withFeedback} evaluados</span>
        <span class="stats-rate">${rate} util</span>
      </div>
      ${Object.keys(stats.byAction).length > 0 ? `
        <div class="stats-actions">
          ${Object.entries(stats.byAction).map(([k, v]) =>
            `<span class="stats-action-tag">${k}: ${v}</span>`).join('')}
        </div>` : ''}
    </details>`;
}

async function verifyConnection(agent) {
  const result = await AgentApiClient.sendAction('ping', { triggerText: 'connection-test' });
  n8nConnected = result.ok;

  const $status = document.getElementById('nx-conn-status');
  if ($status) $status.innerHTML = connDot();

  const $messages = document.getElementById('nx-agent-messages');
  if (!$messages) return;
  const sysMsg = $messages.querySelector('.agent-msg-system');
  if (sysMsg) {
    // Regla 5 del Cerebro: lo interno es interno. Al dueno no le dice nada que
    // el agente este "conectado a n8n via Router" -- eso es nuestra plomeria.
    // En modo dev se conserva tal cual, porque ahi SI es informacion util.
    if (isDevMode()) {
      sysMsg.textContent = n8nConnected
        ? `${agent.name} conectado a n8n via Router. Listo para interactuar.`
        : `${agent.name} en modo local. Las respuestas del chat son simuladas.`;
    } else {
      sysMsg.textContent = n8nConnected
        ? `Hola, soy ${agent.name}. Preguntame por tu negocio: ventas, caja, quien te debe...`
        : `Hola, soy ${agent.name}. Ahora mismo no puedo consultar tus datos; vuelve a intentarlo en un momento.`;
    }
  }
}

// ── Chat handlers ──

function setupChatHandlers(agent) {
  const $form = document.getElementById('nx-agent-form');
  const $input = document.getElementById('nx-agent-input');
  const $messages = document.getElementById('nx-agent-messages');

  $input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      $form.requestSubmit();
    }
  });

  // UIA-T3 (bit #3729): tocar una sugerencia ENVIA la pregunta por la misma ruta que
  // el formulario (requestSubmit), no solo rellena el campo -- dejarlo escrito seria
  // dejarle el trabajo a medias a quien justamente no sabia que preguntar.
  // Las sugerencias desaparecen tras la primera pregunta: ya cumplieron su papel de
  // arranque y despues solo estorbarian sobre la conversacion.
  document.getElementById('nx-sugerencias')?.addEventListener('click', (e) => {
    const btn = e.target.closest('.nx-sug-btn');
    if (!btn) return;
    $input.value = btn.dataset.q || '';
    document.getElementById('nx-sugerencias')?.remove();
    $form.requestSubmit();
  });

  // ── RAG mode switch handler ──
  document.getElementById('nx-rag-mode')?.addEventListener('click', (e) => {
    const btn = e.target.closest('.rag-mode-btn');
    if (!btn) return;
    document.querySelectorAll('.rag-mode-btn').forEach(b => b.classList.remove('rag-mode-btn--active'));
    btn.classList.add('rag-mode-btn--active');
    // Update placeholder based on mode
    const mode = btn.dataset.ragMode;
    const placeholders = {
      'chat': 'Escribe un mensaje o notas para el agente...',
      'consulta': 'Pregunta operativa: ej. ¿qué misiones hay pendientes?',
      'rag': 'Consulta directa al conocimiento RAG...',
      'rag-ai': 'Pregunta al agente usando conocimiento RAG...'
    };
    $input.placeholder = placeholders[mode] || placeholders.chat;
  });

  // Build mission context string for RAG+AI enrichment
  async function buildMissionContext(mission) {
    const linkedTasks = [];
    if (mission.taskIds?.length) {
      for (const tid of mission.taskIds.slice(0, 10)) {
        const tRes = await TaskApiClient.getById(tid);
        if (tRes.ok) {
          const t = tRes.data?.data || tRes.data;
          linkedTasks.push(`- [${t.status}] ${t.title}: ${(t.description || '').substring(0, 100)}`);
        }
      }
    }
    return `\n--- Datos reales de mision ${mission.code} ---\nTitulo: ${mission.title}\nEstado: ${mission.status}\nRecompensa: ${mission.rewardNexos} NXS\nAgentes: ${mission.description?.match(/\[AGENTE:\s*([^\]]+)\]/i)?.[1] || 'sin asignar'}\nInstrucciones: ${(mission.instructionCodes || []).join(', ') || 'ninguna'}\nTareas (${linkedTasks.length}):\n${linkedTasks.join('\n') || '(sin tareas vinculadas)'}\n--- Fin datos mision ---\n`;
  }

  // Recolectar ultimos N mensajes del chat como contexto
  function getChatHistory(limit = 5) {
    const msgs = $messages.querySelectorAll('.agent-msg:not(.agent-msg-system)');
    const history = [];
    const recent = Array.from(msgs).slice(-limit * 2); // user+agent pairs
    for (const msg of recent) {
      const isUser = msg.classList.contains('agent-msg-user');
      const text = msg.textContent?.trim()?.substring(0, 300) || '';
      if (text) history.push({ role: isUser ? 'user' : 'assistant', content: text });
    }
    return history;
  }

  $form.onsubmit = async (e) => {
    e.preventDefault();
    const text = $input.value.trim();
    if (!text) return;

    // UIA-T1 (bit #3729): en modo dueno el switch de 4 modos esta oculto, asi que el
    // default 'chat' lo dejaria atrapado en conversacion pura -- preguntaria "¿quien
    // llego tarde hoy?" y no obtendria SU dato. El modo dueno usa 'consulta', que es
    // el que responde con datos reales del negocio (y el que produce la frase que el
    // ASSIGN pide conservar: "Hoy llego tarde Brayan (10 min)...").
    const activeMode = isDevMode()
      ? (document.querySelector('.rag-mode-btn--active')?.dataset?.ragMode || 'chat')
      : 'consulta';
    const modeLabel = activeMode === 'rag' ? '[RAG]' : activeMode === 'rag-ai' ? '[RAG+AI]' : '';

    appendUserMsg($messages, modeLabel ? `${modeLabel} ${text}` : text);
    $input.value = '';
    $input.style.height = 'auto';
    $input.disabled = true;

    const $typing = showTyping($messages);

    if (activeMode === 'rag') {
      // ── RAG directo: query al vector store ──
      const res = await RagApiClient.query({ question: text, topK: 5 });
      removeEl($typing);
      $input.disabled = false;
      $input.focus();

      if (res.ok) {
        const data = res.data;
        const context = (data.context || 'Sin resultados').replace(/</g, '&lt;');
        const sources = data.sources || [];
        const srcHtml = sources.length > 0
          ? `<div style="margin-top:8px;font-size:0.875rem;color:#6e7681">${sources.map(s =>
              `<span class="area-tag" style="margin:2px">#${s.rank} ${(s.file || '').replace(/</g,'&lt;')} score:${s.score?.toFixed(3) || '?'}</span>`
            ).join('')}</div>`
          : '';
        appendAgentHtml($messages,
          `<div style="font-size:0.875rem;white-space:pre-wrap">${context}</div>${srcHtml}`,
          'rag', 'query');
      } else {
        appendAgentText($messages, `Error RAG: ${res.error}`, 'rag', 'query');
      }

    } else if (activeMode === 'rag-ai') {
      // ── RAG+AI: buscar contexto RAG + datos de mision si se menciona ──
      const ragRes = await RagApiClient.query({ question: text, topK: 3 });
      const ragContext = ragRes.ok ? (ragRes.data?.context || '') : '';
      const ragSources = ragRes.ok ? (ragRes.data?.sources || []) : [];

      // Detectar codigo de mision en el mensaje O cargar misiones asignadas al agente
      let missionDataText = '';
      try {
        const missionsRes = await MissionApiClient.list(1, 100);
        const allMissions = missionsRes.data?.data?.items || missionsRes.data?.items || [];
        const missionMatch = text.match(/([A-Z]+-(?:MISSION|DB|NXS|BANK)-[A-Z0-9-]+)/i);

        if (missionMatch) {
          // Caso 1: usuario menciona mision especifica
          const mission = allMissions.find(m => m.code?.toUpperCase() === missionMatch[1].toUpperCase());
          if (mission) {
            missionDataText = await buildMissionContext(mission);
          }
        } else {
          // Caso 2: cargar misiones asignadas al agente actual (in_progress o active)
          const agentCode = agent?.code || 'ARGEMIRO-001';
          const assigned = allMissions.filter(m =>
            (m.status === 'in_progress' || m.status === 'active') &&
            m.description?.toUpperCase().includes(agentCode)
          );
          if (assigned.length > 0) {
            const summaries = assigned.map(m =>
              `- ${m.code}: ${m.title} [${m.status}] (${m.rewardNexos} NXS, ${m.taskIds?.length || 0} tareas)`
            );
            missionDataText = `\n--- Misiones asignadas a ${agentCode} ---\n${summaries.join('\n')}\n--- Fin misiones asignadas ---\n`;
          }
        }
      } catch { /* silent */ }

      // Historial de chat
      const history = getChatHistory(5);
      const historyText = history.length > 0
        ? `\n--- Historial reciente ---\n${history.map(h => `${h.role}: ${h.content}`).join('\n')}\n--- Fin historial ---\n`
        : '';

      const enrichedPrompt = `[Instruccion: Eres Argemiro, agente director del ecosistema Nexo. Usa el contexto RAG, los datos reales de la mision y el historial para responder con acciones concretas. Si te piden analizar tareas, lista las existentes y propone mejoras especificas. Cita instrucciones por codigo.]${historyText}${missionDataText}\n--- Contexto RAG ---\n${ragContext || 'Sin contexto RAG'}\n--- Fin contexto ---\n\nPregunta: ${text}`;

      const projectKey = document.getElementById('nx-project-select')?.value || null;
      const result = await AgentApiClient.sendChat(enrichedPrompt, sessionId, { projectKey });
      removeEl($typing);
      $input.disabled = false;
      $input.focus();

      if (result.ok) {
        // Mostrar fuentes RAG antes de la respuesta del agente
        if (ragSources.length > 0) {
          const srcHtml = `<div style="font-size:0.875rem;color:#6e7681;margin-bottom:4px">Fuentes RAG: ${ragSources.map(s =>
            `<span class="area-tag" style="margin:2px;font-size:0.875rem">${(s.file || '').replace(/</g,'&lt;')} (${s.score?.toFixed(2) || '?'})</span>`
          ).join('')}</div>`;
          appendAgentHtml($messages, srcHtml, 'rag', 'context');
        }
        renderSmartResponse($messages, result.data, 'n8n', 'rag-ai');
      } else {
        const fallback = processLocalMessage(text, agent);
        appendAgentText($messages, fallback, 'local', 'rag-ai');
      }

    } else if (activeMode === 'consulta') {
      // ── Consulta operativa: datos reales via catálogo de capacidades ──
      // PANEL-ASISTENTE-FORMATO-T1: para que negocio se pregunto; si cambia en el camino, la respuesta no se pinta como actual.
      const consultadoPara = getSelectedBusiness();
      const nombrePara = getSelectedBusinessName();
      const result = await AgentApiClient.sendConsulta(text, sessionId);
      removeEl($typing);
      $input.disabled = false;
      $input.focus();

      if (negocioCambio(consultadoPara, getSelectedBusiness())) {
        appendAgentText($messages, fraseNegocioCambio(nombrePara), 'local', 'consulta');
      } else if (result.ok) {
        const d = result.data;
        if (d.error) {
          // UIA-T1 (bit #3729): en modo dueno el switch de modos esta oculto, asi que
          // un error de consulta lo dejaria SIN SALIDA (no puede cambiar a chat a mano).
          // Se reintenta como conversacion normal: si la pregunta no calzo con ninguna
          // capacidad, al menos Argemiro le responde hablando en vez de un error rojo.
          if (!isDevMode()) {
            const chatRes = await AgentApiClient.sendChat(text, sessionId, {});
            if (chatRes.ok) {
              renderSmartResponse($messages, chatRes.data, 'n8n', 'chat');
            } else {
              appendAgentText($messages,
                'No pude responder eso ahora mismo. Intenta preguntarlo de otra forma, o inténtalo de nuevo en un momento.',
                'local', 'consulta');
            }
          } else {
            const hint = d.hint ? `<div style="margin-top:6px;font-size:0.875rem;color:#8b949e">${d.hint}</div>` : '';
            appendAgentHtml($messages,
              `<div style="color:#f85149">${d.error}</div>${hint}`,
              'n8n', 'consulta');
          }
        } else {
          // Build HTML response: narrativa primero, tabla solo si usuario la pidio
          const rows = d.rows || [];
          const preferredView = d.preferred_view || (d.show_table ? 'table' : 'narrative');
          const hasNarrative = !!d.narrative;
          // UIA-T2 (bit #3729): el codigo de la capacidad (ej. asistencia.tarde_rango)
          // es nomenclatura interna. Al dueno no le dice nada y encabeza la respuesta
          // como si fuera lo importante. Solo en modo dev.
          const caption = (d.capability_used && isDevMode())
            ? `<div style="font-size:0.875rem;color:#6e7681;margin-bottom:6px">Capacidad: <code>${d.capability_used}</code></div>`
            : '';

          // Narrativa (respuesta conversacional natural)
          //
          // UIA-T2: hasta aca llegaba la frase y se pintaba tal cual. El problema de la
          // captura de Josse es que "tal cual" incluye dos cosas que NO son una frase:
          // una plantilla a medio resolver, y el texto generico de n8n que nombra el
          // codigo interno de la capacidad. Las dos se veian como si fueran la respuesta.
          const diag = diagnosticarNarrativa(d.narrative, d.capability_used);

          // UIA-T6: si el resultado repite personas, se agrupa. La captura de Josse era
          // "Brayan (11m), Camilo (9m), Brayan (10m), Camilo (15m)..." -- el mismo nombre
          // cuatro veces y el dueno sumando de cabeza. Va ARRIBA de la frase porque es la
          // respuesta a lo que pregunto; la frase y el detalle quedan igual, debajo.
          const resumenPersonaHtml = renderResumenPorPersona(rows);

          // UIA-T8: una sola fila con muchas columnas se lee como informe (estilo arqueo),
          // no como tabla con scroll horizontal. "Como nos fue ayer" devolvia 11 columnas
          // y en movil no se veia ni la mitad.
          const informeHtml = renderInformeVertical(rows);
          let narrativeHtml = '';
          if (hasNarrative && preferredView === 'narrative' && diag.esFrase) {
            const narrText = String(d.narrative).replace(/</g, '&lt;').replace(/\n/g, '<br>');
            narrativeHtml = `<div style="font-size:0.88rem;line-height:1.5;color:#c9d1d9;padding:6px 0">${narrText}</div>`;
          } else if (hasNarrative && preferredView === 'narrative') {
            // FAIL-LOUD (regla dura del ASSIGN #3795/#3906): no se cae callado al
            // generico. Se dice QUE falta. Ese silencio escondio el bug tres semanas.
            narrativeHtml = isDevMode()
              ? `<div style="border-left:3px solid #d29922;background:#0d1117;border-radius:4px;padding:8px 10px;margin:6px 0;font-size:0.875rem;color:#d29922">
                   <strong>Narrativa sin resolver</strong> — ${escapeHtmlAgente(diag.motivo)}
                   <div style="color:#8b949e;margin-top:4px">Se muestra la tabla porque no hay frase que leer.</div>
                 </div>`
              : `<div style="font-size:0.88rem;line-height:1.5;color:#c9d1d9;padding:6px 0">
                   Tengo el dato pero todavía no sé contártelo en una frase. Te lo muestro en la tabla de abajo.
                 </div>`;
          }

          // Tabla HTML (si el usuario pidio tecnico, o si no hay narrativa)
          //
          // UIA-T2 (bit #3729): en modo dueno, si HAY frase la tabla nunca se pinta
          // desplegada -- queda detras del "ver detalle" de abajo. Solo se muestra
          // abierta cuando no hay narrativa que leer (ahi la tabla ES la respuesta,
          // y dejarlo sin nada seria peor).
          // UIA-T2: la condicion pasa de "hay narrativa" a "hay FRASE". Una plantilla a
          // medio resolver contaba como narrativa y por eso la tabla quedaba escondida
          // detras de un texto que no decia nada. Si no hay frase, la tabla ES la
          // respuesta y tiene que verse.
          const hayFrase = hasNarrative && diag.esFrase;
          let tableHtml = '';
          // Si el informe ya pinto la fila legible, la tabla cruda de la misma fila seria
          // el mismo dato dos veces. Sigue disponible detras de "ver detalle".
          const shouldShowTable = (isDevMode()
            ? (preferredView === 'table' || !hayFrase)
            : !hayFrase) && !informeHtml;
          if (shouldShowTable) {
            if (rows.length > 0) {
              const cols = Object.keys(rows[0]);
              const thCells = cols.map(c => `<th style="padding:4px 8px;text-align:left;border-bottom:1px solid #30363d;font-size:0.875rem;color:#8b949e;white-space:nowrap">${c}</th>`).join('');
              const bodyRows = rows.map(r => {
                const tds = cols.map(c => {
                  let v = r[c] ?? '-';
                  if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}T/.test(v)) v = v.substring(0, 10);
                  const style = c === 'status'
                    ? `color:${v === 'in_progress' ? '#f0883e' : v === 'active' ? '#58a6ff' : v === 'completed' ? '#3fb950' : v === 'pending' ? '#d29922' : '#8b949e'}`
                    : c === 'reward_nexos' || c === 'nxs_total' ? 'color:#d2a8ff;font-weight:600' : '';
                  return `<td style="padding:4px 8px;border-bottom:1px solid #21262d;font-size:0.875rem;${style}">${String(v).replace(/</g,'&lt;')}</td>`;
                }).join('');
                return `<tr>${tds}</tr>`;
              }).join('');
              tableHtml = `<div style="overflow-x:auto;margin-top:6px"><table style="border-collapse:collapse;width:100%"><thead><tr>${thCells}</tr></thead><tbody>${bodyRows}</tbody></table></div>`;
            } else if (!hayFrase) {
              // PANEL-ASISTENTE-FORMATO-T1: sin datos no es cero (ni "sin resultados" a secas).
              tableHtml = `<div style="color:#c9d1d9;font-size:14px">${escapeHtmlAgente(FRASE_SIN_DATOS)}</div>`;
            }
          }

          // UIA-T2: el detalle queda SIEMPRE disponible cuando hay frase + filas (en
          // modo dueno tambien, aunque el preferred_view del backend pida tabla: ahi
          // la frase manda y la tabla pasa detras del toggle). "Ver detalle" en vez de
          // "Ver tabla tecnica": el dueno no esta pidiendo tecnicismos, esta pidiendo
          // el respaldo del dato.
          let toggleHtml = '';
          // Sin frase la tabla ya se pinta desplegada arriba: repetirla detras de un
          // toggle mostraria el mismo dato dos veces.
          const mostrarToggle = hayFrase && rows.length > 0 &&
                                (isDevMode() ? preferredView === 'narrative' : true);
          if (mostrarToggle) {
            const label = isDevMode()
              ? `Ver tabla tecnica (${rows.length} fila${rows.length === 1 ? '' : 's'})`
              : `Ver detalle (${rows.length} registro${rows.length === 1 ? '' : 's'})`;
            toggleHtml = `<details style="margin-top:8px">
              <summary style="cursor:pointer;font-size:0.875rem;color:#58a6ff">${label}</summary>
              <div style="margin-top:4px">${buildConsultaTable(rows)}</div>
            </details>`;
          }

          const summary = rows.length > 0 && preferredView === 'table' && isDevMode()
            ? `<div style="font-size:0.875rem;color:#6e7681;margin-top:6px">${rows.length} resultado${rows.length !== 1 ? 's' : ''}</div>`
            : '';

          // PANEL-ASISTENTE-FORMATO-T1: bajo la conclusion, la linea de contexto (negocio, fuente, hora del dato vs consulta)
          // y lo tomado por defecto; la evidencia sigue plegada en "Ver detalle".
          const contextoHtml = contextoRespuestaHTML(contextoDeConsulta(d, { negocio: nombrePara, consultadoEn: new Date() }));
          appendAgentHtml($messages, `${caption}${resumenPersonaHtml}${informeHtml}${narrativeHtml}${tableHtml}${contextoHtml}${toggleHtml}${summary}`, 'n8n', 'consulta');
        }
      } else {
        // sendConsulta ya dice el motivo en frase de persona (sin negocio, sin permiso, Argemiro caido...).
        appendAgentText($messages, result.error || 'No pude consultar ahora mismo.', 'local', 'consulta');
      }

    } else {
      // ── Chat normal con historial ──
      const history = getChatHistory(5);
      const contextMsg = history.length > 1
        ? `[Historial reciente]\n${history.map(h => `${h.role}: ${h.content}`).join('\n')}\n[Fin historial]\n\n${text}`
        : text;
      const projectKey = document.getElementById('nx-project-select')?.value || null;
      const result = await AgentApiClient.sendChat(contextMsg, sessionId, { projectKey });
      removeEl($typing);
      $input.disabled = false;
      $input.focus();

      if (result.ok) {
        renderSmartResponse($messages, result.data, 'n8n', 'chat');
      } else {
        const fallback = processLocalMessage(text, agent);
        appendAgentText($messages, fallback, 'local', 'chat');
      }
    }
  };
}

// ── Action area (T1 container, modes T2+) ──
// Workspace conversacional adaptativo. Por ahora se monta en debug=true para
// pruebas manuales desde consola: `__nexo.actionArea.showMode('inline-upload', { label: 'Test' })`.
// Cuando T3 (contrato n8n) este listo, parsear metadata de respuestas Patronus
// activara los modos automaticamente.

function setupActionArea() {
  const $aa = document.getElementById('nx-action-area');
  if (!$aa) return;
  const aa = createActionArea($aa, { debug: true });
  // Listener generico — util para logging y para que T3 lo enganche
  aa.on('mode-change', (e) => {
    if (e.to !== 'chat') console.debug('[ActionArea] mode-change', e);
  });
  aa.on('mode-action', (e) => console.debug('[ActionArea] mode-action', e));
}

// ── Watcher toggle + slash commands (T3 ACTION-AREA) ──
// Trigger explicito: el usuario decide cuando activar action-area via toggle + comandos.
// NO hay deteccion de intent automatica — Argemiro sigue respondiendo texto normal.

function setupWatcherAndSlash() {
  const $toggle = document.getElementById('nx-watcher-toggle');
  const $input  = document.getElementById('nx-agent-input');
  const $form   = document.getElementById('nx-agent-form');
  if (!$toggle || !$input || !$form) return;

  // Estado inicial sincronizado con localStorage
  applyWatcherVisual($toggle, $input, isWatcherActive());

  // Click toggle alterna estado
  $toggle.addEventListener('click', () => {
    const next = !isWatcherActive();
    setWatcherActive(next);
    applyWatcherVisual($toggle, $input, next);
    if (next) showWatcherHint();
  });

  // Interceptor: cuando Watcher ON y el input empieza con `/`, parsea comando
  // y activa action-area en vez de enviar a n8n. Sin Watcher, comportamiento normal.
  $form.addEventListener('submit', (e) => {
    if (!isWatcherActive()) return; // Sin Watcher: passthrough al handler de chat normal
    const raw = $input.value;
    const parsed = parseSlashCommand(raw);
    if (!parsed) return; // No es slash command: tambien passthrough

    e.preventDefault();
    e.stopImmediatePropagation();

    const aa = window.__nexo?.actionArea;
    if (!aa) {
      console.warn('[Watcher] actionArea instance no disponible');
      return;
    }

    if (parsed.targetMode === '__help__') {
      const reg = getSlashCommandRegistry();
      const rows = reg.map(c => ({
        Comando: '/' + c.primary,
        Aliases: c.aliases.slice(1).map(a => '/' + a).join(', ') || '—',
        Descripcion: c.description
      }));
      aa.showMode('table-results', { title: '❓ Comandos disponibles', rows });
    } else {
      aa.showMode(parsed.targetMode, parsed.payload);
    }

    $input.value = '';
  }, true); // captura para correr antes que el handler normal
}

function applyWatcherVisual($toggle, $input, active) {
  if (active) {
    $toggle.classList.add('btn-watcher--active');
    $toggle.style.background = 'linear-gradient(135deg,#1f6feb22,#8957e522)';
    $toggle.style.borderColor = '#1f6feb';
    $toggle.style.color = '#79c0ff';
    $toggle.title = 'Modo Watcher ACTIVO · escribe /factura /diseno /onboarding /aprende-descarga /help';
    if (!$input.dataset.originalPlaceholder) $input.dataset.originalPlaceholder = $input.placeholder || '';
    // El watcher corre SIEMPRE, asi que este placeholder tecnico se le aparecia
    // tambien al dueno -- que no sabe que es un watcher ni tiene por que. Los
    // comandos con / siguen funcionando igual: dejan de ANUNCIARSE, nada mas.
    if (isDevMode()) {
      $input.placeholder = '🟢 Watcher ON · / para invocar accion (ej: /factura) o texto normal para chat';
    }
  } else {
    $toggle.classList.remove('btn-watcher--active');
    $toggle.style.background = '';
    $toggle.style.borderColor = '';
    $toggle.style.color = '';
    $toggle.title = 'Modo Watcher: cuando esta ON, comandos slash (/factura, /diseno, /onboarding) invocan el panel de accion sin pasar por el agente';
    // UIA-T1 (bit #3729): el fallback estaba hardcodeado al texto tecnico y pisaba el
    // placeholder simple del modo dueno al montar la vista (el watcher corre siempre).
    $input.placeholder = $input.dataset.originalPlaceholder
      || (isDevMode() ? 'Escribe un mensaje o notas para el agente...' : 'Escribe tu pregunta…');
  }
}

function showWatcherHint() {
  // Hint efímero: tooltip flotante 4s con la lista de comandos
  let $hint = document.getElementById('nx-watcher-hint');
  if ($hint) $hint.remove();
  $hint = document.createElement('div');
  $hint.id = 'nx-watcher-hint';
  Object.assign($hint.style, {
    position: 'fixed', bottom: '24px', right: '24px', zIndex: '900',
    background: '#161b22', border: '1px solid #1f6feb', borderRadius: '8px',
    padding: '12px 16px', maxWidth: '320px', color: '#c9d1d9', fontSize: '0.875rem',
    boxShadow: '0 4px 12px rgba(0,0,0,0.4)', transition: 'opacity 0.3s', opacity: '0'
  });
  const reg = getSlashCommandRegistry();
  $hint.innerHTML = `
    <div style="font-weight:600;color:#79c0ff;margin-bottom:6px">🟢 Modo Watcher ACTIVO</div>
    <div style="margin-bottom:6px;color:#8b949e;font-size:0.875rem">Comandos disponibles:</div>
    ${reg.map(c => `<div style="font-size:0.875rem;padding:1px 0"><code style="background:#21262d;padding:1px 5px;border-radius:3px;color:#79c0ff">/${c.primary}</code> <span style="color:#8b949e">${c.icon} ${c.description}</span></div>`).join('')}
  `;
  document.body.appendChild($hint);
  requestAnimationFrame(() => { $hint.style.opacity = '1'; });
  setTimeout(() => {
    $hint.style.opacity = '0';
    setTimeout(() => $hint.remove(), 300);
  }, 4500);
}

// ── Action handlers ──

function setupActionHandlers(agent) {
  const $bar = document.getElementById('nx-action-bar');
  const $messages = document.getElementById('nx-agent-messages');
  const $input = document.getElementById('nx-agent-input');

  $bar.addEventListener('click', async (e) => {
    const btn = e.target.closest('.btn-action');
    if (!btn) return;
    const action = btn.dataset.action;
    const projectKey = document.getElementById('nx-project-select')?.value || null;

    if (action === 'crear-mision') {
      const bullets = $input.value.trim();
      if (!bullets) {
        appendSystemMsg($messages, 'Escribe notas/bullets en el input y luego presiona "Crear mision".');
        $input.focus();
        return;
      }
      appendUserMsg($messages, `[Crear mision] ${bullets}`);
      const savedBullets = bullets;
      $input.value = '';

      const $typing = showTyping($messages);
      disableActions($bar, true);

      const prompt = `Analiza estas notas y propone una mision con tareas especificas. ` +
        `Responde en formato estructurado: nombre de mision, descripcion breve, y lista de tareas.\n\n` +
        `Notas:\n${savedBullets}` +
        (projectKey ? `\n\nContexto: proyecto ${projectKey}` : '');

      const result = await AgentApiClient.sendChat(prompt, sessionId, {
        projectKey,
        narrative: 'technical'
      });
      removeEl($typing);
      disableActions($bar, false);

      if (result.ok) {
        renderSmartResponse($messages, result.data, 'n8n', 'crear-mision');
      } else {
        appendAgentText($messages, getActionFallback('crear-mision'), 'local', 'crear-mision');
      }
      return;
    }

    appendUserMsg($messages, `[${capitalize(action)}]${projectKey ? ` proyecto: ${projectKey}` : ''}`);
    const $typing = showTyping($messages);
    disableActions($bar, true);

    const params = {
      triggerText: `${action} desde panel v2.2`,
      narrative: 'technical',
      source: 'panel-v2.2'
    };
    if (projectKey) params.project_id = projectKey;

    const result = await AgentApiClient.sendAction(action, params);
    removeEl($typing);
    disableActions($bar, false);

    if (result.ok) {
      renderSmartResponse($messages, result.data, 'n8n', action);
    } else {
      appendAgentText($messages, getActionFallback(action), 'local', action);
    }
  });
}

// ── Smart response rendering ──

function renderSmartResponse($container, data, source, action) {
  if (!data) {
    appendAgentText($container, 'Respuesta vacia del agente.', source, action);
    return;
  }
  // Guard global: strip missions from response unless crear-mision or rag-ai
  if (action !== 'crear-mision' && action !== 'rag-ai' && data && typeof data === 'object') {
    delete data.missions;
    if (data.payload) delete data.payload.missions;
    if (data.result) delete data.result.missions;
  }

  if (typeof data === 'string') {
    appendAgentText($container, data, source, action);
    return;
  }

  if (data.reply || data.output || data.text || data.message) {
    let text = data.reply || data.output || data.text || data.message;

    // rag-ai: reconstruct full reply from structured GPT-4o output
    if (action === 'rag-ai') {
      const parts = [text];
      if (Array.isArray(data.missions) && data.missions.length > 0) {
        parts.push(data.missions.map(m =>
          `**${m.code || ''}** — ${m.title || ''}\n${m.description || ''}`
        ).join('\n\n'));
      }
      if (data.prompt) {
        parts.push('---\n**Prompt de handoff:**\n```\n' + data.prompt + '\n```');
      }
      text = parts.join('\n\n');
    }

    const el = appendAgentHtml($container, renderMarkdown(text), source, action);

    if (action === 'crear-mision' && data.missions && data.missions.length > 0) {
      el.appendChild(buildMissionProposals(data.missions));
    }
    return;
  }

  if (data.context_pack || data.run_id) {
    const el = appendAgentHtml($container, buildRouterCard(data), source, action);
    return;
  }

  if (Array.isArray(data) && data.length > 0) {
    const combined = data.map(item => {
      if (typeof item === 'string') return item;
      return item.reply || item.output || item.text || item.message || JSON.stringify(item);
    }).join('\n\n');
    appendAgentHtml($container, renderMarkdown(combined), source, action);
    return;
  }

  // Intentar extraer contenido legible de payloads anidados
  const nested = data.designPayload?.design || data.payload || data.result || data;
  if (nested && typeof nested === 'object') {
    // Si tiene missions Y la accion es crear-mision, renderizar como propuestas
    if (action === 'crear-mision' && nested.missions && Array.isArray(nested.missions) && nested.missions.length > 0) {
      const el = appendAgentHtml($container, nested.prompt ? renderMarkdown(nested.prompt) : '', source, action);
      el.appendChild(buildMissionProposals(nested.missions));
      return;
    }
    // Renderizar como contenido formateado (no JSON crudo)
    const formatted = formatStructuredData(nested);
    appendAgentHtml($container, formatted, source, action);
    return;
  }

  appendAgentText($container, JSON.stringify(data, null, 2), source, action);
}

function formatStructuredData(obj) {
  const lines = [];
  for (const [key, val] of Object.entries(obj)) {
    if (key === 'prompt') continue; // Skip raw prompt
    if (typeof val === 'string') {
      lines.push(`<div style="margin-bottom:6px"><strong style="color:#58a6ff">${key}:</strong> <span style="color:#c9d1d9">${val.replace(/</g, '&lt;').replace(/\n/g, '<br>')}</span></div>`);
    } else if (Array.isArray(val)) {
      lines.push(`<div style="margin-bottom:6px"><strong style="color:#58a6ff">${key}:</strong></div>`);
      val.forEach((item, i) => {
        if (typeof item === 'string') {
          lines.push(`<div style="padding-left:12px;font-size:0.875rem;color:#c9d1d9">${i + 1}. ${item.replace(/</g, '&lt;')}</div>`);
        } else if (typeof item === 'object') {
          const title = item.title || item.name || item.Titulo || `Item ${i + 1}`;
          lines.push(`<div style="padding-left:12px;margin-top:4px"><strong style="color:#d2a8ff">${String(title).replace(/</g, '&lt;')}</strong></div>`);
          for (const [k, v] of Object.entries(item)) {
            if (k === 'title' || k === 'name' || k === 'Titulo') continue;
            const display = Array.isArray(v) ? v.map(s => String(s).replace(/</g, '&lt;')).join(', ') : String(v).replace(/</g, '&lt;').replace(/\n/g, '<br>');
            lines.push(`<div style="padding-left:24px;font-size:0.875rem;color:#8b949e"><span style="color:#6e7681">${k}:</span> ${display}</div>`);
          }
        }
      });
    } else if (typeof val === 'object' && val !== null) {
      lines.push(`<div style="margin-bottom:6px"><strong style="color:#58a6ff">${key}:</strong></div>`);
      for (const [k, v] of Object.entries(val)) {
        lines.push(`<div style="padding-left:12px;font-size:0.875rem;color:#c9d1d9"><span style="color:#6e7681">${k}:</span> ${String(v).replace(/</g, '&lt;').substring(0, 200)}</div>`);
      }
    }
  }
  return lines.length > 0
    ? `<div style="font-size:0.875rem;line-height:1.5;max-height:400px;overflow-y:auto;padding:8px 0">${lines.join('')}</div>`
    : `<pre style="font-size:0.875rem;max-height:300px;overflow:auto;white-space:pre-wrap;word-break:break-word;color:#8b949e">${JSON.stringify(obj, null, 2).replace(/</g, '&lt;')}</pre>`;
}

function buildRouterCard(data) {
  const q = data.quality || {};
  const obs = data.observability || {};
  const ctx = data.context_pack || {};
  const proj = ctx.project || {};
  const api = ctx.api || {};

  const allReasons = q.fail_reasons || [];
  const realIssues = allReasons.filter(r => /^Q0[1-3]:/.test(r)).map(r => r.replace(/^Q\d+:\s*/, ''));
  const scopePending = allReasons.filter(r => /^Q0[4-6]:/.test(r)).map(r => r.replace(/^Q\d+:\s*/, ''));

  const hasRealIssues = realIssues.length > 0;
  const hasData = proj.project_name || proj.project_code || (proj.technologies || []).length > 0;
  const statusIcon = hasRealIssues ? '&#9888;' : '&#10003;';
  const statusClass = hasRealIssues ? 'router-fail' : 'router-ok';
  const statusLabel = hasRealIssues ? 'Incompleto' : (hasData ? 'Contexto cargado' : 'OK');

  let projectInfo = '';
  if (proj.project_name || proj.project_code) {
    projectInfo = `<div class="router-field"><strong>Proyecto:</strong> ${proj.project_code || ''} ${proj.project_name || ''}</div>`;
  }

  let apiInfo = '';
  if (api.endpoints_total > 0) {
    apiInfo = `<div class="router-field"><strong>API:</strong> ${api.verified_endpoints}/${api.total_endpoints} endpoints verificados</div>`;
  }

  let techInfo = '';
  const techs = proj.technologies || ctx.ownership?.technologies || [];
  if (techs.length > 0) {
    techInfo = `<div class="router-field"><strong>Tech:</strong> ${techs.join(', ')}</div>`;
  }

  return `
    <div class="router-card ${hasRealIssues ? '' : 'router-card-ok'}">
      <div class="router-header">
        <span class="router-status ${statusClass}">${statusIcon} ${statusLabel}</span>
        <span class="router-meta">${obs.completed_steps || 0}/${obs.total_steps || 0} pasos</span>
        <code class="router-run-id">${(data.run_id || '').substring(0, 8)}</code>
      </div>
      ${projectInfo}${techInfo}${apiInfo}
      ${realIssues.length > 0 ? `
        <div class="router-issues">
          <strong>Faltan datos:</strong>
          <ul>${realIssues.map(r => `<li>${r}</li>`).join('')}</ul>
        </div>` : ''}
      ${scopePending.length > 0 ? `
        <div class="router-scope-info">
          <strong>Alcance pendiente:</strong> misiones, tareas e instrucciones se vincularán al usar "Crear mision"
        </div>` : ''}
    </div>`;
}

function buildMissionProposals(missions) {
  const wrap = document.createElement('div');
  wrap.className = 'mission-proposals';
  wrap.innerHTML = `<div class="proposal-header">Misiones propuestas</div>`;

  missions.forEach((m, idx) => {
    // Parse tasks: detect objects with checklist vs plain strings
    const rawTasks = m.tasks || [];
    const rows = []; // [{mode:'task'|'step', text:string}]

    rawTasks.forEach((t, ti) => {
      if (typeof t === 'object' && t !== null) {
        // Structured task object — extract title + checklist items
        const title = t.title ?? t.Title ?? t.Titulo ?? t.name ?? t.Name ??
          (Object.values(t).find(v => typeof v === 'string' && v.length > 3 && v.length < 200)) ?? 'Tarea sin titulo';
        rows.push({ mode: 'task', text: title });
        // Extract checklist if present
        const checklist = t.checklist ?? t.Checklist ?? t.steps ?? t.Steps ?? t.items ?? [];
        if (Array.isArray(checklist)) {
          checklist.forEach(s => {
            const stepName = typeof s === 'string' ? s : (s.name ?? s.Name ?? s.title ?? s.text ?? '');
            if (stepName) rows.push({ mode: 'step', text: stepName.replace(/^[\s✅⬜\d.]+/, '').trim() });
          });
        }
      } else {
        // Plain string — first one is task, rest are steps of it
        const text = String(t);
        rows.push({ mode: ti === 0 ? 'task' : 'step', text });
      }
    });

    // If no rows parsed, add one default task
    if (rows.length === 0) rows.push({ mode: 'task', text: 'Tarea principal' });

    const card = document.createElement('div');
    card.className = 'proposal-card';
    card.dataset.proposalIdx = idx;
    card.innerHTML = `
      <div class="proposal-title-row">
        <input type="text" class="proposal-title-input" value="${escapeHtmlAgente(m.title || 'Sin titulo')}" />
      </div>
      <div class="proposal-desc-row">
        <input type="text" class="proposal-desc-input" value="${escapeHtmlAgente(m.description)}" placeholder="Descripcion..." />
      </div>
      <div class="proposal-task-list">
        ${rows.map((r, ri) => `
          <div class="proposal-task-row" data-task-idx="${ri}">
            <select class="proposal-row-mode" style="font-size:0.875rem;padding:1px 2px;background:#161b22;color:#8b949e;border:1px solid #30363d;border-radius:3px;width:50px">
              <option value="step" ${r.mode === 'step' ? 'selected' : ''}>Paso</option>
              <option value="task" ${r.mode === 'task' ? 'selected' : ''}>Tarea</option>
            </select>
            <input type="text" class="proposal-task-input" value="${escapeHtmlAgente(r.text)}" style="flex:1" />
            <button class="btn-icon btn-remove-task" title="Quitar">&times;</button>
          </div>`).join('')}
      </div>
      <div class="proposal-add-row" style="display:flex;gap:6px">
        <button class="btn-add-task" data-mode="step">+ Paso</button>
        <button class="btn-add-task" data-mode="task" style="opacity:0.7">+ Tarea</button>
      </div>
      <div style="padding:4px 0;font-size:0.875rem;color:#6e7681">
        Tarea = se crea independiente. Paso = se agrega al checklist de la tarea anterior.
      </div>
      <div class="proposal-actions">
        <button class="btn-accept-mission" data-idx="${idx}">Aceptar y crear</button>
      </div>`;
    wrap.appendChild(card);
  });

  wrap.addEventListener('click', async (e) => {
    const removeBtn = e.target.closest('.btn-remove-task');
    if (removeBtn) {
      const row = removeBtn.closest('.proposal-task-row');
      if (row) row.remove();
      return;
    }

    const addBtn = e.target.closest('.btn-add-task');
    if (addBtn) {
      const mode = addBtn.dataset.mode || 'step';
      const card = addBtn.closest('.proposal-card');
      const list = card.querySelector('.proposal-task-list');
      const newRow = document.createElement('div');
      newRow.className = 'proposal-task-row';
      newRow.innerHTML = `
        <select class="proposal-row-mode" style="font-size:0.875rem;padding:1px 2px;background:#161b22;color:#8b949e;border:1px solid #30363d;border-radius:3px;width:50px">
          <option value="step" ${mode === 'step' ? 'selected' : ''}>Paso</option>
          <option value="task" ${mode === 'task' ? 'selected' : ''}>Tarea</option>
        </select>
        <input type="text" class="proposal-task-input" value="" placeholder="${mode === 'task' ? 'Nueva tarea...' : 'Nuevo paso...'}" style="flex:1" />
        <button class="btn-icon btn-remove-task" title="Quitar">&times;</button>`;
      list.appendChild(newRow);
      newRow.querySelector('.proposal-task-input').focus();
      return;
    }

    const acceptBtn = e.target.closest('.btn-accept-mission');
    if (acceptBtn) {
      const idx = parseInt(acceptBtn.dataset.idx, 10);
      const card = acceptBtn.closest('.proposal-card');
      const editedProposal = readProposalFromCard(card, missions[idx]);
      await acceptMissionProposal(editedProposal, acceptBtn, card);
    }
  });

  return wrap;
}

function readProposalFromCard(card, original) {
  const title = card.querySelector('.proposal-title-input')?.value.trim() || original.title || '';
  const description = card.querySelector('.proposal-desc-input')?.value.trim() || original.description || '';

  // Read hierarchical structure: taskGroups = [{title, steps:[]}]
  const rows = card.querySelectorAll('.proposal-task-row');
  const taskGroups = [];
  let currentGroup = null;

  rows.forEach(row => {
    const mode = row.querySelector('.proposal-row-mode')?.value || 'step';
    const text = row.querySelector('.proposal-task-input')?.value.trim() || '';
    if (!text) return;

    if (mode === 'task') {
      currentGroup = { title: text, steps: [] };
      taskGroups.push(currentGroup);
    } else {
      // 'step' - add to current group, or create implicit group if none exists
      if (!currentGroup) {
        currentGroup = { title: title || 'Tarea principal', steps: [], implicit: true };
        taskGroups.push(currentGroup);
      }
      currentGroup.steps.push(text);
    }
  });

  // Backward compat: flat list of all item texts
  const tasks = Array.from(rows)
    .map(r => r.querySelector('.proposal-task-input')?.value.trim())
    .filter(Boolean);

  return {
    ...original,
    title,
    description,
    tasks,
    taskGroups
  };
}

async function acceptMissionProposal(proposal, btn, card) {
  const projectKey = document.getElementById('nx-project-select')?.value || '';
  const $messages = document.getElementById('nx-agent-messages');
  const taskTitles = proposal.tasks || [];
  if (taskTitles.length === 0) {
    showProposalError(card, 'Agrega al menos una tarea');
    return;
  }

  const groups = proposal.taskGroups || [];
  // Lo que se va a crear, armado antes de preguntar: un grupo es una tarea con su checklist; sin grupos, cada item es una tarea.
  const porCrear = groups.length > 0
    ? groups.map((group) => {
      const steps = group.steps.map((s, i) => ({ name: s.substring(0, 200), completed: false, order: i }));
      return {
        title: (group.implicit ? (proposal.title || 'Tarea del agente') : group.title).substring(0, 200),
        description: (proposal.description || `Tarea generada por agente para mision: ${proposal.title || ''}`).substring(0, 5000),
        ...(steps.length > 0 ? { steps } : {})
      };
    })
    : taskTitles.map((title) => ({
      title: title.substring(0, 200),
      description: `Tarea generada por agente para mision: ${proposal.title || ''}`
    }));

  const missionCode = (proposal.code || `MISSION-${Date.now()}`).substring(0, 60);

  // SALV-PUERTA-API-T1 (Ares #24167, opcion ii): UNA clave y UN por que para todo el lote, escritos por la persona.
  // Cada tarea sale <CLAVE>-Tn por la puerta, dentro de la mision que se crea primero (la puerta pide mision viva).
  const campos = await pedirPuertaEnDialogo({
    titulo: `Crear la mision ${missionCode} y sus ${porCrear.length} tarea(s)`,
    explicacion: 'Las tareas entran por la puerta con una sola clave y un solo por que para todo el lote. Si ya hay tareas parecidas, mejor no crear este lote.',
    misionFija: missionCode,
    claveInicial: claveSugerida(proposal.title || missionCode),
    boton: 'Aceptar y crear'
  });
  if (!campos) return;

  btn.disabled = true;
  btn.textContent = 'Creando...';

  try {
    // 1. La mision primero: sin ella viva, la puerta no deja crear ninguna tarea (PUERTA_MISION).
    const missionRes = await MissionApiClient.create({
      code: missionCode,
      title: (proposal.title || 'Mision del agente').substring(0, 200),
      description: (proposal.description || '').substring(0, 5000),
      rewardNexos: proposal.rewardNexos || 0,
      status: 'active',
      taskIds: [],
      instructionCodes: proposal.instructionCodes || []
    });
    if (!missionRes.ok) throw new Error(`no se creo la mision: ${missionRes.error}`);
    const mission = missionRes.data?.data || missionRes.data;

    // 2. Las tareas, una por una por la puerta; si una no pasa, se para y se dice por que.
    const createdTaskIds = [];
    let frenada = '';
    for (const t of porCrear) {
      const res = await TaskApiClient.create({
        ...t,
        status: 'pending',
        priority: 'medium',
        project: projectKey || 'panel-v2',
        tags: ['agent-generated'],
        ...campos
      });
      if (!res.ok) { frenada = motivoPuerta(res); break; }
      const task = res.data?.data || res.data;
      if (task?.id) createdTaskIds.push(task.id);
    }

    // 3. Se ligan las que entraron.
    if (createdTaskIds.length && mission?.id) {
      await MissionApiClient.update(mission.id, { taskIds: createdTaskIds });
    }

    card.classList.add('proposal-accepted');
    card.innerHTML = `
      <div class="proposal-title"><code class="mission-code">${missionCode}</code> ${proposal.title}</div>
      <div class="proposal-status ${frenada ? 'proposal-status-err' : 'proposal-status-ok'}">
        Mision creada: <a href="#/missions/${mission?.id || ''}" class="proposal-link">${missionCode}</a>
        <span class="proposal-detail">${createdTaskIds.length} de ${porCrear.length} tarea(s) vinculadas</span>
      </div>`;
    if (frenada) showProposalError(card, `la puerta freno el resto: ${frenada}`);

    if ($messages) {
      appendSystemMsg($messages, `Mision "${proposal.title}" creada con ${createdTaskIds.length} de ${porCrear.length} tareas. Ver en Misiones.`);
    }

  } catch (err) {
    btn.disabled = false;
    btn.textContent = 'Reintentar';
    showProposalError(card, err.message || 'No se pudo crear');
  }
}

function showProposalError(card, msg) {
  card.classList.add('proposal-error');
  let errDiv = card.querySelector('.proposal-err');
  if (!errDiv) {
    errDiv = document.createElement('div');
    errDiv.className = 'proposal-err';
    card.appendChild(errDiv);
  }
  errDiv.textContent = `Error: ${msg}`;
}

// ── Message DOM helpers ──

function appendUserMsg($container, text) {
  const div = document.createElement('div');
  div.className = 'agent-msg agent-msg-user';
  div.textContent = text;
  $container.appendChild(div);
  $container.scrollTop = $container.scrollHeight;
}

function appendSystemMsg($container, text) {
  const div = document.createElement('div');
  div.className = 'agent-msg agent-msg-system';
  div.textContent = text;
  $container.appendChild(div);
  $container.scrollTop = $container.scrollHeight;
}

function appendAgentText($container, text, source, action) {
  const div = createAgentBubble(source);
  div.textContent = text;
  appendSourceTag(div, source);
  $container.appendChild(div);
  $container.scrollTop = $container.scrollHeight;
  const logId = logInteraction(text, action, source);
  attachFeedback(div, logId);
}

function appendAgentHtml($container, html, source, action) {
  const div = createAgentBubble(source);
  div.innerHTML = `<div class="doc-viewer">${html}</div>`;
  appendSourceTag(div, source);
  $container.appendChild(div);
  $container.scrollTop = $container.scrollHeight;
  const logId = logInteraction(div.textContent, action, source);
  attachFeedback(div, logId);
  if (source === 'n8n' || action === 'rag-ai') {
    attachBitacoraButton(div, div.textContent, action);
  }
  // ASIST-FIX-VISTA: si la respuesta trae el resumen por persona (UIA-T12), las zonas
  // .nx-dia-accion reservadas reciben el boton de correccion auditada. No-op para
  // roles sin permiso o sin negocio seleccionado; un fallo aqui jamas rompe el chat.
  try { bindDiaAcciones(div); } catch { /* el resumen queda solo-lectura, como antes */ }
  return div;
}

function createAgentBubble(source) {
  const div = document.createElement('div');
  div.className = 'agent-msg agent-msg-agent';
  div.style.cssText = 'word-break:break-word';
  return div;
}

function appendSourceTag(el, source) {
  if (!source) return;
  const tag = document.createElement('span');
  tag.className = `msg-source msg-source-${source}`;
  tag.textContent = source;
  el.appendChild(tag);
}

function showTyping($container) {
  const div = document.createElement('div');
  div.className = 'agent-msg agent-msg-typing';
  div.innerHTML = '<span class="typing-dot"></span><span class="typing-dot"></span><span class="typing-dot"></span>';
  $container.appendChild(div);
  $container.scrollTop = $container.scrollHeight;
  return div;
}

function removeEl(el) {
  if (el?.parentNode) el.parentNode.removeChild(el);
}

function disableActions($bar, disabled) {
  $bar.querySelectorAll('.btn-action').forEach(b => b.disabled = disabled);
}

// ── Feedback ──

function attachFeedback(msgEl, logId) {
  const wrap = document.createElement('div');
  wrap.className = 'msg-feedback';
  wrap.innerHTML = `
    <button class="btn-feedback" data-vote="up" title="Util">&#x1F44D;</button>
    <button class="btn-feedback" data-vote="down" title="No util">&#x1F44E;</button>`;
  wrap.addEventListener('click', (e) => {
    const btn = e.target.closest('.btn-feedback');
    if (!btn) return;
    const helpful = btn.dataset.vote === 'up';
    AgentInteractionLog.setFeedback(logId, helpful);
    wrap.innerHTML = helpful
      ? '<span class="feedback-done feedback-up">Util</span>'
      : '<span class="feedback-done feedback-down">No util</span>';
  });
  msgEl.appendChild(wrap);
}

function attachBitacoraButton(msgEl, responseText, action) {
  const btn = document.createElement('button');
  btn.className = 'btn-bitacora-log';
  btn.textContent = 'Registrar en bitacora';
  btn.title = 'Registrar esta interaccion en la bitacora de Argemiro';
  btn.onclick = () => openBitacoraModal(responseText, action);
  msgEl.appendChild(btn);
}

function openBitacoraModal(responseText, action) {
  const existing = document.getElementById('bitacora-modal');
  if (existing) existing.remove();

  const summary = responseText.substring(0, 150).replace(/\n/g, ' ').trim();
  const defaultTitle = `[DONE] ${action === 'rag-ai' ? 'Consulta RAG+AI' : action === 'query' ? 'Consulta RAG' : 'Chat'}: ${summary}`;
  const defaultNotas = JSON.stringify({
    type: 'agent.done',
    agent: 'ARGEMIRO-001',
    action,
    summary: summary.substring(0, 200)
  });

  const modal = document.createElement('div');
  modal.id = 'bitacora-modal';
  modal.className = 'modal-overlay';
  modal.innerHTML = `
    <div class="modal-box" style="max-width:560px">
      <div class="modal-header">
        <span class="modal-title">Registrar en bitacora</span>
        <button class="modal-close" id="bitacora-modal-close">&#x2715;</button>
      </div>
      <div class="modal-body">
        <label class="detail-label" style="margin-bottom:4px;display:block">Titulo</label>
        <input type="text" id="bitacora-titulo" class="form-input" value="${escapeHtmlAgente(defaultTitle)}" style="margin-bottom:12px">
        <label class="detail-label" style="margin-bottom:4px;display:block">Notas (JSON)</label>
        <textarea id="bitacora-notas" class="form-input form-textarea" rows="4" style="font-family:monospace;font-size:0.875rem">${defaultNotas.replace(/</g, '&lt;')}</textarea>
      </div>
      <div class="modal-footer">
        <button class="btn-ghost btn-sm" id="bitacora-cancel">Cancelar</button>
        <button class="btn-primary btn-sm" id="bitacora-approve">Aprobar y registrar</button>
      </div>
    </div>`;

  document.body.appendChild(modal);

  const close = () => modal.remove();
  document.getElementById('bitacora-modal-close').onclick = close;
  document.getElementById('bitacora-cancel').onclick = close;
  modal.addEventListener('click', (e) => { if (e.target === modal) close(); });

  document.getElementById('bitacora-approve').onclick = async () => {
    const titulo = document.getElementById('bitacora-titulo').value.trim();
    const notas = document.getElementById('bitacora-notas').value.trim();
    if (!titulo) return;

    const approveBtn = document.getElementById('bitacora-approve');
    approveBtn.disabled = true;
    approveBtn.textContent = 'Registrando...';

    const result = await BitacoraApiClient.create({
      origen: 'argemiro-001',
      titulo,
      notas,
      tareasOk: {},
      payload: {}
    });

    if (result.ok) {
      approveBtn.textContent = 'Registrado';
      approveBtn.style.background = '#238636';
      // Toast de confirmacion visible en el chat
      const $messages = document.getElementById('nx-agent-messages');
      if ($messages) {
        const toast = document.createElement('div');
        toast.className = 'agent-msg agent-msg-system';
        toast.textContent = `Bitacora registrada (origen: argemiro-001)`;
        toast.style.color = '#238636';
        $messages.appendChild(toast);
        $messages.scrollTop = $messages.scrollHeight;
      }
      setTimeout(close, 800);
    } else {
      approveBtn.textContent = 'Error';
      approveBtn.style.background = '#f85149';
      approveBtn.disabled = false;
    }
  };
}

function logInteraction(text, action, source) {
  return AgentInteractionLog.log({
    userMsg: `[${action}]`,
    agentResponse: typeof text === 'string' ? text.substring(0, 500) : String(text).substring(0, 500),
    action,
    source
  });
}

// ── Formatting ──
// renderMarkdown() imported from shared/DocumentViewer.js (marked.js)

function capitalize(str) {
  return str.charAt(0).toUpperCase() + str.slice(1);
}

// ── Fallbacks ──

function getActionFallback(action) {
  const map = {
    pensadero: 'n8n no disponible. El Pensadero necesita conexion para recopilar contexto del proyecto.',
    analizar: 'n8n no disponible. Selecciona un proyecto e intenta de nuevo cuando n8n este conectado.',
    'crear-mision': 'n8n no disponible. Para crear misiones desde notas necesito Patronus. Puedes crearlas manualmente en Misiones.'
  };
  return map[action] || 'Accion no disponible sin conexion a n8n.';
}

function processLocalMessage(text, agent) {
  const l = text.toLowerCase();
  if (l.includes('mision') || l.includes('misión'))
    return 'Usa el boton "Crear mision" con tus notas en el input. Patronus analizara y propondra mision + tareas.';
  if (l.includes('tarea') || l.includes('task'))
    return 'Modulo Tareas disponible. A futuro creare tareas desde instrucciones parametrizadas.';
  if (l.includes('instruccion') || l.includes('instruction'))
    return 'Modulo Instrucciones permite crear protocolos reutilizables que puedo ejecutar.';
  if (l.includes('hola') || l.includes('hello') || l.includes('hey'))
    return `Hola! Soy ${agent.name}. Estoy en modo local (sin n8n). Preguntame sobre misiones, tareas o instrucciones.`;
  if (l.includes('estado') || l.includes('status'))
    return `Activo en modo local. n8n: ${n8nConnected ? 'conectado' : 'desconectado'}. Usa los botones de accion para operaciones avanzadas.`;
  return `[Modo local] Mensaje recibido. Con n8n activo, Patronus dara respuestas inteligentes.`;
}

// ── Knowledge/RAG handlers para Argemiro ──

// ── Bitacora panel para agentes no-chat ──

async function loadAgentBitacora(agent) {
  const el = document.getElementById('nx-agent-bitacora');
  if (!el) return;
  try {
    const res = await BitacoraApiClient.list(50);
    const payload = res.data?.data ?? res.data;
    const allEntries = payload?.items ?? (Array.isArray(payload) ? payload : []);
    const code = (agent.code || agent.name || '').toLowerCase().replace('-001', '');
    const entries = allEntries.filter(e =>
      e.origen && e.origen.toLowerCase().includes(code)
    );
    if (entries.length === 0) {
      el.innerHTML = `<p class="empty-state">Sin entradas de bitacora para ${agent.name}</p>`;
      return;
    }
    el.innerHTML = `
      <div class="bitacora-list">
        <div class="bitacora-header">
          <span>Bitacora de ${agent.name}</span>
          <span class="bitacora-count">${entries.length} entrada${entries.length !== 1 ? 's' : ''}</span>
        </div>
        ${entries.slice(0, 20).map(e => {
          const ts = e.tsUtc || e.createdAt || '';
          const dateStr = ts ? formatearFecha(ts, { dateStyle: 'short', timeStyle: 'short' }, { parte: 'todo' }) : '';
          return `
          <div class="bitacora-entry">
            <div class="bitacora-entry-header">
              <code style="font-size:0.875rem;color:#58a6ff">${e.origen || ''}</code>
              <span style="font-size:0.875rem;color:#6e7681">${dateStr}</span>
            </div>
            <div style="font-size:0.875rem;font-weight:500;color:#c9d1d9">${(e.titulo || '').replace(/</g, '&lt;')}</div>
            ${e.notas ? `<div style="font-size:0.875rem;color:#8b949e;margin-top:4px;line-height:1.4;max-height:60px;overflow:hidden">${e.notas.substring(0, 200).replace(/</g, '&lt;')}</div>` : ''}
          </div>`;
        }).join('')}
      </div>`;
  } catch (err) {
    el.innerHTML = `<p class="empty-state">Error: ${err.message}</p>`;
  }
}
